import {
  GENERATE_AUTH_TOKEN,
  PAYMENT_SERVER_URL,
  REBILL_BILLING_STATUS_URL,
  STRIPE_BILLING_PORTAL_URL,
  STRIPE_CHECKOUT_URL,
} from '../config';

// Fetch al server de pagos de Atlas con el auth de Atlas SIEMPRE puesto.
// Usar SIEMPRE esto para pegarle al payment server (en vez de `fetch` pelado) →
// garantiza que todo request lleve el `Authorization: <GENERATE_AUTH_TOKEN>` que
// el server valida, y el `Content-Type: application/json` por defecto.
//
// `pathOrUrl` puede ser un path relativo ('/rebill/change-plan') o una URL
// completa (se usa tal cual). Headers extra pisan los defaults si hace falta.
export function paymentServerFetch(
  pathOrUrl: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = /^https?:\/\//.test(pathOrUrl)
    ? pathOrUrl
    : `${PAYMENT_SERVER_URL}${pathOrUrl}`;
  return fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
      ...(init.headers || {}),
    },
  });
}

// ─── Stripe: checkout + billing portal ──────────────────────────────────────
// Antes eran webhooks de n8n (`stripe-start` / `billing-portal`), que ante
// cualquier problema devolvían un 4xx/5xx pelado. El payment server devuelve
// errores TIPADOS: `{ ok:false, error, message }`. Ese `message` es dev-facing
// ("Usuario sin empresa: <uuid>", "Plan inválido: x") → NO se muestra nunca;
// lo que se muestra es la traducción del código `error`.
const STRIPE_ERROR_MESSAGES: Record<string, string> = {
  invalid_plan: 'El plan seleccionado no está disponible. Actualizá la página y probá de nuevo.',
  bad_request: 'No pudimos procesar el pedido. Actualizá la página y probá de nuevo.',
  user_not_found: 'No encontramos tu cuenta. Cerrá sesión, volvé a entrar y probá de nuevo.',
  company_not_found: 'No encontramos tu empresa. Escribinos y lo resolvemos.',
  // La empresa ya está activa en MercadoPago/Rebill: cobrarle también por Stripe
  // la dejaría con dos suscripciones. El cambio de plan va por el flujo de AR.
  platform_mismatch:
    'Tu suscripción se gestiona con otro medio de pago. Cambiá tu plan desde la sección "Plan".',
  // Billing portal sin haber pagado nunca por Stripe: no hay portal que abrir.
  no_stripe_customer:
    'Todavía no tenés pagos con tarjeta internacional, así que no hay portal de facturación para abrir.',
  stripe_not_configured:
    'Los pagos con tarjeta no están disponibles en este momento. Probá de nuevo en unos minutos.',
  unauthorized: 'No pudimos validar tu sesión. Cerrá sesión, volvé a entrar y probá de nuevo.',
};

/** Traduce la respuesta de error del payment server a un texto mostrable al usuario. */
async function stripeErrorMessage(resp: Response, fallback: string): Promise<string> {
  try {
    const data = await resp.json();
    const code = typeof data?.error === 'string' ? data.error : '';
    if (code && STRIPE_ERROR_MESSAGES[code]) return STRIPE_ERROR_MESSAGES[code];
  } catch {
    // Cuerpo vacío o no-JSON (502 de Render, timeout, etc.) → mensaje genérico.
  }
  return fallback;
}

/** POST /stripe/checkout → URL de la Checkout Session de Stripe (redirect). */
export async function fetchStripeCheckoutUrl(body: {
  user_id: string;
  plan: string;
  con_descuento?: boolean;
  cupon_id?: string;
}): Promise<string> {
  const fallback = 'No se pudo iniciar el pago. Probá de nuevo en unos minutos.';
  const resp = await paymentServerFetch(STRIPE_CHECKOUT_URL, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(await stripeErrorMessage(resp, fallback));
  const data = await resp.json();
  if (!data?.url) throw new Error('No se recibió una URL válida para el checkout');
  return data.url as string;
}

// ─── Facturación AFIP de la suscripción (solo AR) ───────────────────────────
/**
 * POST /facturar-pendientes → dispara la emisión de las facturas AFIP de los pagos
 * pendientes del usuario. Se llama DESPUÉS de cargar los datos fiscales (opt-in).
 * Best-effort / NO bloqueante: si falla, el job de renovaciones las emite después,
 * así que nunca tiramos un error visible al usuario.
 */
export async function facturarPendientes(
  userId: string,
): Promise<{ ok: boolean; emitidas?: number; errores?: number }> {
  try {
    const resp = await paymentServerFetch('/facturar-pendientes', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    if (!resp.ok) return { ok: false };
    const data = await resp.json().catch(() => ({}));
    return { ok: true, emitidas: data?.emitidas, errores: data?.errores };
  } catch {
    return { ok: false };
  }
}

/**
 * POST /padron → consulta el padrón de AFIP por CUIT (valida existencia + trae razón social
 * y, si el padrón la provee, la condición IVA). `ok` indica que la consulta se pudo hacer;
 * `exists` que el CUIT existe. Si `ok` es false (backend caído/no deployado) el form no
 * bloquea nada — el usuario carga a mano.
 */
export async function consultarPadron(cuit: string): Promise<{
  ok: boolean;
  exists: boolean;
  razonSocial: string | null;
  condicionIva: string | null;
}> {
  try {
    const resp = await paymentServerFetch('/padron', {
      method: 'POST',
      body: JSON.stringify({ cuit }),
    });
    if (!resp.ok) return { ok: false, exists: false, razonSocial: null, condicionIva: null };
    const data = await resp.json().catch(() => null);
    return {
      ok: !!data?.ok,
      exists: !!data?.exists,
      razonSocial: data?.razonSocial ?? null,
      condicionIva: data?.condicionIva ?? null,
    };
  } catch {
    return { ok: false, exists: false, razonSocial: null, condicionIva: null };
  }
}

/**
 * POST /factura-pdf → descarga el PDF de una factura emitida (con CAE + QR).
 * Trae el binario y dispara la descarga en el navegador.
 */
export async function descargarFacturaPDF(facturaId: string, nombreArchivo?: string): Promise<void> {
  const resp = await paymentServerFetch('/factura-pdf', {
    method: 'POST',
    body: JSON.stringify({ factura_id: facturaId }),
  });
  if (!resp.ok) throw new Error('No se pudo generar el PDF de la factura.');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Nombre legible para el usuario (ej. "Factura B-0001-00000123.pdf"); fallback al id.
  a.download = `${(nombreArchivo || `factura-${facturaId}`).replace(/[/\\]/g, '-')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Rebill: ¿se le puede cobrar la tarjeta guardada? ───────────────────────
// Rebill exige documento (DNI/CUIT) y teléfono para cobrar con `cardId` en AR. El
// checkout embebido pide el DNI en CADA pago pero no lo deja en el customer, así
// que un cliente puede tener tarjeta guardada y aun así ser incobrable por API.
// Cuando pasa eso, el one-click sale, Rebill lo rechaza con
// `required_customer_document` y el cliente queda sin poder pagar
// (ADR-2026-08-07-one-click-cambio-plan-sin-documento).
//
// Este chequeo se hace ANTES de ofrecer el one-click. Es la mitad del front del
// endpoint que el payment server tiene desde el 2026-07-30 y nunca se cableó.

/** Faltantes posibles que devuelve el server. `tarjeta` incluye "no hay ninguna". */
export type RebillFaltante = 'tarjeta' | 'telefono' | 'documento';

export interface RebillBillingStatus {
  ok: true;
  /** `false` = la empresa no es de Rebill → el one-click no aplica y no hay nada que chequear. */
  aplica: boolean;
  tiene_tarjeta?: boolean;
  datos?: { telefono: boolean; documento: boolean; direccion: boolean };
  faltantes?: RebillFaltante[];
  /** `true` sólo si no falta NADA: recién ahí el cobro con tarjeta guardada puede salir. */
  puede_cobro_directo?: boolean;
}

/**
 * POST /rebill/billing-status. Devuelve `null` ante CUALQUIER problema (red, 5xx,
 * timeout, body raro): el llamador debe interpretarlo como "no sé" y quedarse con
 * el comportamiento de siempre, nunca como "le falta algo".
 *
 * Timeout propio y corto: esto corre en el camino crítico del checkout (bloquea el
 * loader), y el server consulta la API de Rebill dos veces. Si tarda, preferimos
 * seguir sin la respuesta antes que dejar al cliente mirando un spinner.
 */
export async function fetchRebillBillingStatus(
  userId: string,
  timeoutMs = 6000,
): Promise<RebillBillingStatus | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await paymentServerFetch(REBILL_BILLING_STATUS_URL, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
      signal: controller.signal,
    });
    if (!resp.ok) return null;
    const data = (await resp.json().catch(() => null)) as RebillBillingStatus | null;
    return data?.ok === true ? data : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** POST /stripe/billing-portal → URL del portal de facturación de Stripe (redirect). */
export async function fetchStripeBillingPortalUrl(userId: string): Promise<string> {
  const fallback = 'No se pudo abrir el portal de facturación.';
  const resp = await paymentServerFetch(STRIPE_BILLING_PORTAL_URL, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
  if (!resp.ok) throw new Error(await stripeErrorMessage(resp, fallback));
  const data = await resp.json();
  if (!data?.url) throw new Error('URL inválida recibida');
  return data.url as string;
}

// ─── Rebill: cambiar la tarjeta de la suscripción (link hosteado) ────────────
/**
 * POST /rebill/card-update-link → URL hosteada de Rebill para CAMBIAR la tarjeta de la suscripción
 * (el front redirige ahí). NO cobra: es un reemplazo de tarjeta. El endpoint devuelve
 * `{ ok:true, url }` | `{ ok:false, error }` (no_rebill_subscription / rebill_unavailable). Ante
 * cualquier fallo tira Error para que el llamador muestre un aviso; el `error` del server es
 * dev-facing y NO se muestra crudo.
 */
export async function fetchRebillCardUpdateLink(userId: string): Promise<string> {
  const resp = await paymentServerFetch('/rebill/card-update-link', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
  const data = (await resp.json().catch(() => null)) as
    | { ok?: boolean; url?: string; error?: string }
    | null;
  if (!resp.ok || !data?.ok || !data.url) {
    throw new Error('No pudimos abrir el cambio de tarjeta. Probá de nuevo en unos minutos.');
  }
  return data.url;
}
